import actionTypes from "data/actions/actionTypes";
import Constants from "data/constants";
import { produce } from "immer";

const initialState = {
    nodes: [],
    status: Constants.NONE,
    error: undefined,
    isRoot: undefined,
    isBrowsable: undefined,
    itemUri: null,
    filter: null,
    isDeep: false,
    page: 0,
    pageSize: 0,
    minPage: null,
    maxPage: null,
    totalCount: 0,
};

const isNewRequest = (current, next) =>
    next.refresh ||
    current.itemUri !== next.itemUri ||
    (current.filter ?? "") !== (next.filter ?? "") ||
    (current.isDeep ?? false) !== (next.isDeep ?? false);

const treeReducer = (state = initialState, action) =>
    produce(state, (draft) => {
        switch (action.type) {
            case actionTypes.TREE_SET_STATUS:
                draft.status = action.payload.status;
                break;
            case actionTypes.TREE_FETCH_REQUEST:
                // reset state if new request
                if (isNewRequest(draft, action.payload)) {
                    draft.nodes = [];
                    draft.page = 0;
                    draft.pageSize = 0;
                    draft.totalCount = 0;
                    draft.minPage = null;
                    draft.maxPage = null;
                }
                draft.itemUri = action.payload.itemUri;
                draft.status = action.payload.status;
                draft.filter = action.payload.filter;
                draft.isDeep = action.payload.isDeep;
                break;
            case actionTypes.TREE_FETCH_RESULT:
                const append = action.payload.page > draft.page;
                // calculate min/max pages
                draft.minPage = Math.min(
                    draft.minPage ?? action.payload.page,
                    action.payload.page
                );
                draft.maxPage = Math.max(
                    draft.maxPage ?? action.payload.page,
                    action.payload.page
                );
                draft.page = action.payload.page;
                draft.pageSize = action.payload.pageSize;
                // update state
                draft.status = action.payload.status;
                draft.error = action.payload.error;
                draft.isRoot = action.payload.isRoot;
                draft.isBrowsable = action.payload.isBrowsable;
                draft.totalCount = Math.max(
                    draft.totalCount,
                    action.payload.totalCount
                );
                const uniqueNodes = action.payload.nodes.filter(
                    (n) => !draft.nodes.some((dn) => dn.id === n.id)
                );
                draft.nodes = append
                    ? [...draft.nodes, ...uniqueNodes]
                    : [...uniqueNodes, ...draft.nodes];
                break;
            /* istanbul ignore next */
            case actionTypes.PERSIST_PURGE:
                draft = initialState;
                break;
            default:
                break;
        }
        return draft;
    });

export default treeReducer;

// ------ selectors

export const treeNodesSelector = (state: Object) => state.tree.nodes;
export const treeStatusSelector = (state: Object) => state.tree.status;
export const treeErrorSelector = (state: Object) => state.tree.error;
export const treeIsRootSelector = (state: Object) => !!state.tree.isRoot;
export const treeIsBrowsableSelector = (state: Object) =>
    !!state.tree.isBrowsable;
export const treeTotalCountSelector = (state: Object) => state.tree.totalCount;
export const treeNextPageSelector = (state: Object) => {
    if (state.tree.page === -1) return null;
    const next =
        Math.max(
            state.tree.maxPage ?? /* istanbul ignore next */ state.tree.page,
            state.tree.page
        ) + 1;
    return state.tree.totalCount / state.tree.pageSize > next ? next : null;
};
export const treePrevPageSelector = (state: Object) => {
    if (state.tree.page === -1) return null;
    const prev =
        Math.min(
            state.tree.minPage ?? /* istanbul ignore next */ state.tree.page,
            state.tree.page
        ) - 1;
    return prev >= 0 ? prev : null;
};
